import { addTo, getOrCreate } from '../../../util/index.js';
import { overlayInterestPeriods } from '../interest/periods.js';
import { addYears, expiryCut, NEVER } from './dates.js';
import { amountOn, dayAmounts, paidOnDay, unwriteDay, writeOffDay, } from './ledger.js';
import { roundHalfAway } from './rounding.js';
import { baseStreamOf, dayIndex, rootCount, streamOf } from './streams.js';
// Days of the interest's derived arrays: through `until`.
const dayCount = (interest, until) => Math.max(0, dayIndex(interest, until)) + 1;
// A day not yet expired that holds or booked derived interest.
const isLiveDerived = (days, i) => days.dead[i] === 0 && (days.exact[i] !== 0 || days.written[i] !== 0);
// The interest's rates over [from, to), its interest periods laid over them.
export const effectiveRates = (interest, from, to) => {
    const rates = interest.rates(from, to);
    return interest.periods.length > 0
        ? overlayInterestPeriods(rates, interest.periods, from, to)
        : rates;
};
const derivedDays = (entry, stream) => getOrCreate(entry.interest.derived, stream, () => {
    const length = dayCount(entry.interest, entry.caseState.until);
    return {
        exact: new Float64Array(length),
        written: new Float64Array(length),
        dead: new Uint8Array(length),
        exactThrough: new Float64Array(length),
        writtenThrough: new Float64Array(length),
        changedFrom: 0,
        short: [],
    };
});
// What its own limitation wrote off at the last expiry leaves its base from
// its capitalization on: exactly what was left of it, its derived interest
// already written off counted at its exact amount, not in whole øre. Such a
// day takes nothing more from later write-offs. An øre booked on a day is
// what the running total of it and the days of none before it grew by (D27),
// so those days expire with it, their derived interest counted with its.
export const expiredCapitalized = (entry) => {
    const interest = entry.interest;
    const ledger = entry.ledger;
    const left = new Map();
    for (const tranche of ledger.lastExpired) {
        const byDay = getOrCreate(left, tranche.stream, () => new Map());
        for (let day = tranche.from; day < tranche.to; day++) {
            const amount = amountOn(tranche, day);
            if (amount > 0) {
                addTo(byDay, day, amount - paidOnDay(tranche, day));
            }
        }
    }
    const removals = [];
    for (const [stream, byDay] of left) {
        const days = derivedDays(entry, stream);
        // Kills a day, returning what of its derived interest is not booked.
        const expire = (day) => {
            const i = dayIndex(interest, day);
            const unbooked = days.exact[i] - days.written[i];
            if (day < ledger.capitalizedBefore) {
                addTo(interest.unbooked, stream, -unbooked);
            }
            // The running total of the days after it changes.
            if (days.exact[i] !== 0 || days.written[i] !== 0) {
                interest.dirty = true;
            }
            days.exact[i] = 0;
            days.written[i] = 0;
            days.dead[i] = 1;
            days.changedFrom = Math.min(days.changedFrom, i);
            return unbooked;
        };
        const isDayOfNone = (day) => day >= interest.start &&
            !byDay.has(day) &&
            days.dead[dayIndex(interest, day)] === 0 &&
            dayAmounts(ledger, day, stream).amount === 0;
        for (const [day, remainder] of byDay) {
            let amount = remainder - expire(day);
            for (let before = day - 1; isDayOfNone(before); before--) {
                amount -= expire(before);
            }
            if (day < ledger.capitalizedBefore && amount > 0) {
                const from = interest.capitalizations.find((date) => date > day);
                removals.push({
                    baseStream: baseStreamOf(interest, stream, from),
                    from,
                    amount,
                });
            }
        }
    }
    return removals;
};
// Each day's rate per day over [from, to): as charged, and as each pending
// reinstated stream accrues it (the rate without its voided period).
const accruedRates = (entry, from, to) => {
    const interest = entry.interest;
    const rates = effectiveRates(interest, from, to);
    const shadows = interest.shadows.filter((shadow) => shadow.voidedAt > from);
    const charged = new Float64Array(Math.max(0, to - from));
    const reinstated = new Map(shadows.map((shadow) => [shadow.index, new Float64Array(charged.length)]));
    for (let rateIndex = 0; rateIndex < rates.length; rateIndex++) {
        const rate = rates[rateIndex];
        const start = Math.max(from, rate.time ?? from);
        const end = Math.min(to, rates[rateIndex + 1]?.time ?? to);
        for (let day = start; day < end; day++) {
            const index = day - from;
            charged[index] = rate.rate / interest.daysAYear;
            for (const shadow of shadows) {
                const own = rate.voidedPeriodId === shadow.periodId;
                reinstated.get(shadow.index)[index] =
                    (own ? (rate.baseRate ?? rate.rate) : rate.rate) / interest.daysAYear;
            }
        }
    }
    return { charged, reinstated, shadows };
};
// D30: interest derived from capitalized interest expires with it. Interest
// is linear in its base, so removing the expired remainders from the base
// from their capitalization on removes exactly what they produced, at any
// depth: one pass over the days since, in which the removed amount grows at
// each capitalization by what it produced on days still alive. A day that
// expired on its own clock adds nothing, as its own expiry removed what it
// produced. Of a day partly paid, the paid share of what it loses stays: paid
// interest never expires. The exact amounts are booked in whole øre by
// `bookDerived` once something reads the ledger.
//
// Reinstated interest (D8) compounds on its own pending base until its
// voidedAt, and as part of the base from then on; on its voided period it
// also accrues at the rate without the period on the base, less what the base
// accrued at the rate charged. Each root compounds on its own.
export const writeOffDerived = (caseState, entry, removals, at) => {
    const interest = entry.interest;
    const ledger = entry.ledger;
    const roots = rootCount(interest);
    const removedFrom = removals.toSorted((a, b) => a.from - b.from);
    if (removedFrom.length === 0) {
        return;
    }
    const from = Math.max(removedFrom[0].from, interest.start);
    const to = Math.min(at, caseState.until, interest.end ?? NEVER);
    if (from >= to) {
        return;
    }
    interest.rateDays ??= accruedRates(entry, interest.start, caseState.until);
    const { charged, reinstated, shadows } = interest.rateDays;
    // By stream.
    const streams = streamOf(interest, Math.max(0, ...interest.shadows.map((shadow) => shadow.index)) + 1, 0);
    const removed = new Float64Array(streams);
    const uncapitalized = new Float64Array(streams);
    const unbooked = new Float64Array(streams);
    const derived = [];
    const baseRemoved = caseState.options.appendHist === true
        ? (interest.baseRemoved ??= new Float64Array(dayCount(interest, caseState.until)))
        : null;
    // The share of a day's interest a write-off takes, as `tally` takes it
    const unpaidShare = (stream, day, index) => {
        const days = (derived[stream] ??= derivedDays(entry, stream));
        if (days.dead[index] === 1) {
            return 0;
        }
        if (day >= ledger.paidThrough) {
            return 1;
        }
        const { amount: accrued, paid } = dayAmounts(ledger, day, stream);
        const current = accrued - days.exact[index];
        return current > 0 ? Math.max(0, (current - paid) / current) : 0;
    };
    const tally = (stream, day, index, amount) => {
        if (amount <= 0) {
            return;
        }
        const days = (derived[stream] ??= derivedDays(entry, stream));
        if (days.dead[index] === 1) {
            return;
        }
        let lost = amount;
        if (day < ledger.paidThrough) {
            const { amount: accrued, paid } = dayAmounts(ledger, day, stream);
            const current = accrued - days.exact[index];
            lost = current > 0 ? (amount * (current - paid)) / current : 0;
            if (lost <= 0) {
                return;
            }
        }
        uncapitalized[stream] += lost;
        days.exact[index] += lost;
        days.changedFrom = Math.min(days.changedFrom, index);
        if (day < ledger.capitalizedBefore) {
            unbooked[stream] += lost;
        }
        interest.dirty = true;
    };
    let nextRemoval = 0;
    let nextCapitalization = interest.capitalizations.findIndex((date) => date >= from);
    for (let day = from; day < to; day++) {
        for (const shadow of shadows) {
            if (day < shadow.voidedAt) {
                continue;
            }
            for (let root = 0; root < roots; root++) {
                const stream = streamOf(interest, shadow.index, root);
                removed[root] += removed[stream];
                removed[stream] = 0;
            }
        }
        if (interest.capitalizations[nextCapitalization] === day) {
            for (let stream = 0; stream < streams; stream++) {
                if (uncapitalized[stream] !== 0) {
                    removed[baseStreamOf(interest, stream, day)] += uncapitalized[stream];
                    uncapitalized[stream] = 0;
                }
            }
            nextCapitalization++;
        }
        while (removedFrom[nextRemoval]?.from <= day) {
            const removal = removedFrom[nextRemoval++];
            if (removal.from === day) {
                removed[removal.baseStream] += removal.amount;
            }
        }
        const index = dayIndex(interest, day);
        for (let root = 0; root < roots; root++) {
            const fromBase = removed[root];
            if (baseRemoved !== null && fromBase !== 0) {
                baseRemoved[index] += fromBase * unpaidShare(root, day, index);
            }
            tally(root, day, index, fromBase * charged[index]);
            for (const shadow of shadows) {
                if (day >= shadow.voidedAt) {
                    continue;
                }
                const stream = streamOf(interest, shadow.index, root);
                const rate = reinstated.get(shadow.index)[index];
                tally(stream, day, index, removed[stream] * rate + fromBase * (rate - charged[index]));
            }
        }
    }
    for (let stream = 0; stream < streams; stream++) {
        if (unbooked[stream] !== 0) {
            addTo(interest.unbooked, stream, unbooked[stream]);
        }
    }
};
// Fixed interest's expired remainder leaves the base of each compound voucher
// it compounded in, for the days it did.
export const writeOffFixedDerived = (caseState, fixed, amount, at) => {
    fixed.compoundedIn.forEach(({ entry, from }, index) => {
        const interest = entry.interest;
        const base = streamOf(interest, 0, interest.roots.indexOf(fixed) + 1);
        const removals = [{ baseStream: base, from, amount }];
        const next = fixed.compoundedIn[index + 1];
        if (next !== undefined) {
            removals.push({ baseStream: base, from: next.from, amount: -amount });
        }
        writeOffDerived(caseState, entry, removals, at);
    });
};
// Books derived interest in whole øre, as D27 books accrual: per stream, over
// the days still alive, in date order, each day writes off what the rounded
// running total of the exact amounts grows by on it. When a write-off moves
// the rounding to an earlier day, a later day gives its øre back, so every
// running total stays within half an øre of exact. A day's booking depends
// only on the days before it, so booking stops at day index `end`. Before the
// first day changed since the last booking, only a day that was short can
// have anything due; until one of them books something, nothing after it
// changes.
const bookDerived = (entry, stream, days, end) => {
    const interest = entry.interest;
    const ledger = entry.ledger;
    let capitalizedBooked = 0;
    const bookDay = (i, due) => {
        const day = interest.start + i;
        const booked = due > 0
            ? writeOffDay(ledger, day, stream, due)
            : -unwriteDay(ledger, day, stream, -due);
        days.written[i] += booked;
        if (day < ledger.capitalizedBefore) {
            capitalizedBooked += booked;
        }
        return booked;
    };
    let start = Math.min(days.changedFrom, end);
    for (const i of days.short) {
        if (i >= start) {
            break;
        }
        const before = i > 0 ? days.writtenThrough[i - 1] : 0;
        const due = roundHalfAway(days.exactThrough[i]) - before - days.written[i];
        const booked = bookDay(i, due);
        if (booked !== 0) {
            days.writtenThrough[i] += booked;
            days.changedFrom = Math.min(days.changedFrom, i + 1);
            days.short = days.short.filter((day) => day < i || (day === i && booked !== due));
            start = i + 1;
            break;
        }
    }
    if (start < end) {
        let exact = start > 0 ? days.exactThrough[start - 1] : 0;
        let written = start > 0 ? days.writtenThrough[start - 1] : 0;
        const short = days.short.filter((day) => day < start);
        for (let i = start; i < end; i++) {
            if (isLiveDerived(days, i)) {
                exact += days.exact[i];
                const due = roundHalfAway(exact) - written - days.written[i];
                if (due !== 0 && bookDay(i, due) !== due) {
                    short.push(i);
                }
                written += days.written[i];
            }
            days.exactThrough[i] = exact;
            days.writtenThrough[i] = written;
        }
        days.short = short;
        days.changedFrom = end;
    }
    addTo(interest.unbooked, stream, -capitalizedBooked);
};
// Books what derived interest is pending, before anything reads the ledger.
export const settleDerived = (entry) => {
    const interest = entry.interest;
    if (interest === null || !interest.dirty) {
        return;
    }
    for (const [stream, days] of interest.derived) {
        bookDerived(entry, stream, days, days.exact.length);
    }
    interest.dirty = false;
};
// The day before which a day's limitation can pass at `at`: the rolling cut,
// or past the last day of a group whose limitation passed.
const judgedBefore = (ledger, at) => {
    let before = expiryCut(at, ledger.periods.ordinary);
    for (const group of ledger.groups) {
        if (group.at < at) {
            for (const tranche of group.tranches) {
                before = Math.max(before, tranche.to);
            }
        }
    }
    return before;
};
// Books what derived interest is pending on the days whose limitation can
// pass at `at`, so whether they have anything left to expire does not depend
// on when later days are booked.
export const settleJudged = (entry, at) => {
    const interest = entry.interest;
    if (interest === null || !interest.dirty) {
        return;
    }
    const before = dayIndex(interest, judgedBefore(entry.ledger, at));
    for (const [stream, days] of interest.derived) {
        bookDerived(entry, stream, days, Math.min(before, days.exact.length));
    }
};
// While later days wait to be booked, the next day with derived interest to
// pass its limitation is a stop, where booking may give it an øre back. None
// passes once limitation is frozen.
export const nextDerivedExpiry = (caseState, entry, after) => {
    const interest = entry.interest;
    const ledger = entry.ledger;
    const from = Math.max(0, dayIndex(interest, expiryCut(after, ledger.periods.ordinary)));
    let first = NEVER;
    for (const days of interest.derived.values()) {
        for (let i = from; i < Math.min(first, days.exact.length); i++) {
            if (isLiveDerived(days, i)) {
                first = i;
                break;
            }
        }
    }
    if (first === NEVER) {
        return NEVER;
    }
    const passes = addYears(interest.start + first, ledger.periods.ordinary) + 1;
    return passes > (caseState.options.limitationFrozenAt ?? NEVER)
        ? NEVER
        : passes;
};
// Capitalizes what accrued before `to`, its unbooked derived interest with it.
export const capitalize = (entry, to) => {
    const interest = entry.interest;
    const ledger = entry.ledger;
    for (const [stream, days] of interest.derived) {
        const end = Math.min(dayIndex(interest, to), days.exact.length);
        for (let i = Math.max(0, dayIndex(interest, ledger.capitalizedBefore)); i < end; i++) {
            if (days.dead[i] === 0) {
                addTo(interest.unbooked, stream, days.exact[i] - days.written[i]);
            }
        }
    }
    ledger.capitalizedBefore = to;
    interest.capitalizations.push(to);
};
// What of the interest capitalized in `stream` is derived but not yet booked.
// A closed ledger has nothing left to book it from.
export const unbookedIn = (entry, stream) => entry.ledger.closed === null ? (entry.interest.unbooked.get(stream) ?? 0) : 0;
//# sourceMappingURL=derived.js.map